import {
  ButtonDesigns,
  getFontCss,
  styleConstants,
  Tag,
  TagSizes,
  themeConstants,
} from '@paywithextend/components'
import cn from 'classnames'
import BaseModal, { BaseModalProps } from 'ext-ui/components/BaseModal/BaseModal'
import { spacing } from 'ext-ui/style/spacing'
import moment, { Moment } from 'moment'
import React, { ReactElement, useEffect, useState } from 'react'
import { CalendarMonth } from 'react-dates'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { TitledCell } from 'src/components/ListBox/ListBox'
import LoadingPopupContent from 'src/components/LoadingPopupContent/LoadingPopupContent'
import ModalDialogForm from 'src/components/ModalDialogForm/ModalDialogForm'
import NotFoundPopupContent from 'src/components/NotFoundPopupContent/NotFoundPopupContent'
import RadioItems from 'src/components/RadioItems/RadioItems'
import UserAvatarAndName from 'src/components/UserAvatarAndName/UserAvatarAndName'
import { OrgExpenseReportCycleType } from 'src/enums'
import Trns from 'src/localization/Trns'
import { usePatchOrgExpenseReportCycle, useQueryOrgExpenseReportCycle } from 'src/queries/orgExpenseReports.queries'
import { LocationState } from 'src/redux/createRootReducer'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { css } from 'styled-components'

import style from './SetupExpenseReportCycleModal.style'

interface RadioOptionProps {
  title: React.ReactElement
  description: React.ReactElement
  selected?: boolean
}

const RadioOption: React.FC<RadioOptionProps> = (props): React.ReactElement => {
  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 4px;
      `}
    >
      <span
        css={css`
          ${getFontCss(
            14,
            themeConstants.colors.font_primary,
            props.selected
              ? styleConstants.fontFamilies.medium
              : styleConstants.fontFamilies.regular,
          )};
        `}
      >
        {props.title}
      </span>
      <span
        css={css`
          ${getFontCss(
            12,
            styleConstants.colors.gray_darkest,
            styleConstants.fontFamilies.regular,
          )};
        `}
      >
        {props.description}
      </span>
    </div>
  )
}

type SetupExpenseReportCycleModalProps = BaseModalProps & {
  cycleId: string
}

export default function SetupExpenseReportCycleModal(props: SetupExpenseReportCycleModalProps): ReactElement {
  const {
    className = '',
    variant = 'popup',
    isOpen,
    onClose,
    cycleId,
  } = props

  const user = useSelector(selectUserListModelFromState)
  const { data: cycle, error: fetchFailure } = useQueryOrgExpenseReportCycle({
    cycleId,
    enabled: cycleId !== '',
  })
  const history = useHistory<LocationState>()
  const { analytics } = useAnalyticsContext()

  const [selectedIndex, setSelectedIndex] = useState<number>(0)
  const [selectedDate, setSelectedDate] = useState<number>(1)

  const {
    mutate: patchOrgExpenseReportCycle,
    error: putFailure,
    isPending,
  } = usePatchOrgExpenseReportCycle()

  useEffect(() => {
    if (cycle === undefined) return
    else if (cycle.cycleType === OrgExpenseReportCycleType.Monthly)
      setSelectedIndex(0)
    else if (cycle.cycleType === OrgExpenseReportCycleType.DayOfMonth) {
      setSelectedIndex(1)
      setSelectedDate(cycle.dayOfMonth ?? 1)
    }
  }, [cycle])

  const handleFormSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    patchOrgExpenseReportCycle(
      {
        id: cycleId,
        body: {
          cycleType:
            selectedIndex === 0
              ? OrgExpenseReportCycleType.Monthly
              : OrgExpenseReportCycleType.DayOfMonth,
          dayOfMonth: selectedIndex === 0 ? undefined : selectedDate,
        },
      },
      {
        // https://tanstack.com/query/latest/docs/framework/react/guides/mutations#consecutive-mutations
        // these callbacks are only to be used if the mutation is only going to be called once per mount
        // like in this case, where it's a form submission that closes the modal
        // otherwise use the onSuccess callback where `useExtendMutation` is called
        onSuccess: ({ cycle }) => {
          onClose()
          analytics?.track('rcn cycle updated', {
            card_holder: cycle?.accountUserId,
            original_cycle_type: cycle?.cycleType,
            updated_cycle_type:
              selectedIndex === 0
                ? OrgExpenseReportCycleType.Monthly
                : OrgExpenseReportCycleType.DayOfMonth,
          })
        },
      },
    )
  }


  return (
    <BaseModal
      className={cn('SetupExpenseReportCycleModal', className)}
      variant={variant}
      isOpen={isOpen}
      onClose={onClose}
      css={style}
    >
      {user?.hasOrgBookkeeperPermissions && !fetchFailure ? (
        cycle === undefined ? (
          <LoadingPopupContent />
        ) : (
          <ModalDialogForm
            formOnSubmit={handleFormSubmit}
            actionButtonLabel={
              <span
                css={css`
                  display: flex;
                  align-items: center;
                `}
              >
                <Trns source={'Save'} />
              </span>
            }
            actionButtonLoading={isPending}
            actionButtonDesign={ButtonDesigns.designCta}
            cancelButtonLabel={<Trns source={'Cancel'} />}
            cancelButtonOnClick={(): void => {
              history.goBack()
            }}
            showCancelAsLink
            isFormValid
            error={putFailure?.error}
          >
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: flex-start;
              `}
            >
              <span
                css={css`
                  ${getFontCss(
                    32,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.medium,
                  )};
                  margin-bottom: 24px;
                `}
              >
                <Trns source={'Set up statement cycle'} />
                {/* TODO add tooltip */}
              </span>
              <div
                css={css`
                  width: 100%;
                  display: flex;
                  flex-direction: row;
                  padding: ${spacing(5)};
                  border: 1px ${styleConstants.colors.gray_medium} solid;
                  border-radius: ${styleConstants.borderRadius.box};
                  background-color: ${styleConstants.colors.gray_light};
                  gap: 40px;
                  justify-content: space-between;
                `}
              >
                <TitledCell
                  isTitle
                  style={css`
                    max-width: 60%;
                  `}
                  label={<Trns source={'Account Name'} />}
                  content={
                    <span>
                      <span
                        css={css`
                          padding-inline-end: 8px;
                          ${getFontCss(
                            16,
                            themeConstants.colors.font_primary,
                            styleConstants.fontFamilies.medium,
                          )};
                        `}
                      >
                        {cycle.accountName}
                      </span>
                      <Tag
                        text={cycle.accountLast4?.toString()}
                        size={TagSizes.Medium}
                      />
                    </span>
                  }
                />
                <TitledCell
                  spreadCells
                  style={css`
                    max-width: 50%;
                  `}
                  label={<Trns source={'Account Holder'} />}
                  content={
                    <UserAvatarAndName
                      textCss={css`
                        ${getFontCss(
                          16,
                          themeConstants.colors.font_primary,
                          styleConstants.fontFamilies.regular,
                        )};
                      `}
                      user={{
                        id: cycle.accountUserId,
                        fullNameOrEmail:
                          cycle.accountUserFullName || cycle.accountUserEmail,
                        initials:
                          `${cycle.accountUserFirstName?.slice(0, 1)}${cycle.accountUserLastName?.slice(0, 1)}` ||
                          cycle.accountUserEmail[0],
                        avatarUrl: cycle.accountUserAvatarUrl,
                        hasFullName: true,
                      }}
                    />
                  }
                />
              </div>
              <span
                css={css`
                  ${getFontCss(
                    20,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.medium,
                  )};
                  margin-top: 40px;
                  margin-bottom: 20px;
                `}
              >
                <Trns source={'Choose your closing date type'} />
              </span>
              <RadioItems
                css={css`
                  width: 100%;
                  margin-bottom: 40px;
                  div:hover {
                    background-color: transparent;
                  }
                `}
                noBorder
                alignStart
                items={[
                  <RadioOption
                    key={0}
                    title={<Trns source={'Monthly'} />}
                    description={
                      <Trns
                        source={
                          'For statements that always start on the 1st and close on the last day of the month'
                        }
                      />
                    }
                    selected={selectedIndex === 0}
                  />,
                  <div
                    key={1}
                    css={css`
                      display: flex;
                      flex-direction: column;
                      gap: 20px;
                    `}
                  >
                    <RadioOption
                      title={<Trns source={'Day of Month'} />}
                      description={
                        <Trns
                          source={
                            'For statements that always close on the same calendar day every month'
                          }
                        />
                      }
                      selected={selectedIndex === 1}
                    />
                    <div
                      css={css`
                        .CalendarMonth_caption {
                          display: none;
                        }
                        .CalendarDay {
                          ${getFontCss(
                            14,
                            styleConstants.colors.font_gray_dark,
                            styleConstants.fontFamilies.regular,
                          )}
                          border: 1px solid ${styleConstants.colors
                            .gray_medium};
                          background-color: ${styleConstants.colors
                            .gray_lightest};
                          opacity: ${selectedIndex === 1 ? 1 : 0.5};
                          cursor: ${selectedIndex === 1
                            ? 'pointer'
                            : 'default'};

                          // react-dates does not expose any kind of actual focused date state,
                          // but does allow you to control which date is focusable
                          ${selectedIndex === 1 &&
                          css`
                            &:hover,
                            &[tabindex='0'] {
                              background-color: ${themeConstants.colors
                                .cta_dark};
                              ${getFontCss(
                                14,
                                styleConstants.colors.white,
                                styleConstants.fontFamilies.regular,
                              )}
                            }
                          `}
                        }
                      `}
                    >
                      <CalendarMonth
                        month={moment('20260201', 'YYYYMMDD')} // 28 day month with the 1st at the beginning of the week
                        onDayClick={(date: Moment): void => {
                          setSelectedDate(date.date())
                        }}
                        focusedDate={moment(
                          (20260200 + selectedDate).toString(),
                          'YYYYMMDD',
                        )}
                        horizontalMonthPadding={0}
                      />
                    </div>
                  </div>,
                ]}
                currentIndex={selectedIndex}
                setIndex={setSelectedIndex}
              />
            </div>
          </ModalDialogForm>
        )
      ) : (
        <NotFoundPopupContent />
      )}
    </BaseModal>
  )
}
